import { Injectable, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { QuickNavigationRoute, quickNavigationRouteEntries } from '@core/config/quick-navigation-routes.config';
import { RouteSegment } from '@core/enums/route-segment.enum';
import { BreakpointService } from '@core/services/breakpoint.service';
import {
  selectFeatureFlags,
  selectIsDashboardEnabled,
  selectIsGlobalChargersEnabled,
  selectIsGlobalVehiclesEnabled,
  selectIsOptimoTenant,
  selectPermissions,
} from '@core/stores/auth/auth.selectors';
import { TenantsStore } from '@core/stores/tenants/tenants.store';

import { SiteStoreModel } from '@domain/dashboard/dashboard.models';
import { SiteTypeStoreModelEnum } from '@domain/single-site/single-site.enums';

import { DialogRef, DialogService } from '@shared/modules/dialog';

import { QuickNavigationDialogComponent } from './quick-navigation-dialog.component';

export interface QuickNavigationOption {
  id: string;
  label: string;
  route: string[];
}

@Injectable({
  providedIn: 'root',
})
export class QuickNavigationService {
  readonly #dialogService = inject(DialogService);
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #tenantsStore = inject(TenantsStore);
  readonly #breakpointService = inject(BreakpointService);
  readonly #permissions = this.#store.selectSignal(selectPermissions);
  readonly #featureFlags = this.#store.selectSignal(selectFeatureFlags);
  readonly #dashboardEnabled = this.#store.selectSignal(selectIsDashboardEnabled);
  readonly #globalChargersEnabled = this.#store.selectSignal(selectIsGlobalChargersEnabled);
  readonly #globalVehiclesEnabled = this.#store.selectSignal(selectIsGlobalVehiclesEnabled);
  readonly #isOptimoTenant = this.#store.selectSignal(selectIsOptimoTenant);
  #dialogRef: DialogRef<QuickNavigationDialogComponent> | null = null;

  public readonly options = computed(() => {
    const sites = this.#tenantsStore.sites() ?? [];

    return quickNavigationRouteEntries.flatMap(([id, route]): QuickNavigationOption[] => {
      if (route.scope === 'global') {
        if (!this.#isAvailable(route, sites)) {
          return [];
        }

        return [{ id, label: this.#translateService.instant(route.labelTranslationKey), route: route.segments }];
      }

      return sites
        .filter((site) => this.#isAvailable(route, sites, site))
        .map((site) => ({
          id: `${id}-${site.id}`,
          label: `${site.name} · ${this.#translateService.instant(route.labelTranslationKey)}`,
          route:
            id === 'kiosk' || id === 'kiosk-new'
              ? [RouteSegment.Root, route.segments[0], site.id]
              : [RouteSegment.Root, RouteSegment.Sites, site.id, ...route.segments],
        }));
    });
  });

  public open(): void {
    if (this.#dialogRef) {
      return;
    }

    this.#tenantsStore.ensureTenantsLoaded();
    this.#dialogRef = this.#dialogService.open(QuickNavigationDialogComponent, {
      ariaLabel: this.#translateService.instant('quick-navigation.title'),
      autoFocus: '#quick-navigation-input',
      width: 'min(640px, calc(100vw - 32px))',
      height: 'min(680px, calc(100vh - 32px))',
      panelClass: 'h-full',
    });
    this.#dialogRef.afterDismissed().subscribe(() => {
      this.#dialogRef = null;
    });
  }

  #isAvailable(route: QuickNavigationRoute, sites: SiteStoreModel[], site?: SiteStoreModel): boolean {
    return (
      this.#hasRequiredPermissions(route) &&
      this.#hasRequiredFeatures(route) &&
      this.#hasRequiredSettings(route) &&
      this.#hasValidSite(route, sites, site)
    );
  }

  #hasRequiredPermissions(route: QuickNavigationRoute): boolean {
    const permissions = this.#permissions();
    if (route.permissions && !route.permissions.some((permission) => permissions?.includes(permission))) {
      return false;
    }
    if (
      route.additionalPermissionGroups &&
      !route.additionalPermissionGroups.every((permissionGroup) => permissionGroup.some((permission) => permissions?.includes(permission)))
    ) {
      return false;
    }

    return true;
  }

  #hasRequiredFeatures(route: QuickNavigationRoute): boolean {
    return !route.featureFlags || route.featureFlags.some((flag) => this.#featureFlags()?.includes(flag));
  }

  #hasRequiredSettings(route: QuickNavigationRoute): boolean {
    return (
      (!route.requiresDashboardEnabled || !!this.#dashboardEnabled()) &&
      (!route.requiresGlobalChargersEnabled || !!this.#globalChargersEnabled()) &&
      (!route.requiresGlobalVehiclesEnabled || !!this.#globalVehiclesEnabled()) &&
      (!route.requiresOptimoTenant || this.#isOptimoTenant()) &&
      (!route.requiresDesktop || this.#breakpointService.lgSignal())
    );
  }

  #hasValidSite(route: QuickNavigationRoute, sites: SiteStoreModel[], site?: SiteStoreModel): boolean {
    const telemetryFeaturesEnabled =
      site?.features?.telemetryFeaturesEnabled ?? sites.some((item) => item.features?.telemetryFeaturesEnabled);

    return (
      (!route.requiresTelemetryFeaturesEnabled || telemetryFeaturesEnabled) &&
      (!route.requiresFleetSite || site?.siteType === SiteTypeStoreModelEnum.Fleet) &&
      (!route.requiresGridViewEnabled || site?.features?.gridViewEnabled === true)
    );
  }
}
